import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Alert,
  CircularProgress,
  FormHelperText,
} from "@mui/material";
import { useState } from "react";

interface NewUserDetailsDialogProps {
  isOpen: boolean;
  email: string;
  handleClose: () => void;
  handleSubmit: (firstName: string, lastName: string, password: string) => Promise<void>;
}

const NewUserDetailsDialog = ({
  isOpen,
  email,
  handleClose,
  handleSubmit,
}: NewUserDetailsDialogProps) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  // Firebase password validation - at least 6 characters
  const validatePassword = (pwd: string) => {
    if (pwd.length < 6) {
      setPasswordError("Password must be at least 6 characters long");
      return false;
    }
    setPasswordError(null);
    return true;
  };

  const onSubmit = async () => {
    // Validate password
    if (!validatePassword(password)) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      await handleSubmit(firstName, lastName, password);
      setSuccess(true);
      setFirstName("");
      setLastName("");
      setPassword("");
      // Close dialog after showing success for a moment
      setTimeout(() => {
        handleClose();
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create user. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={loading ? undefined : handleClose}>
      <DialogTitle>New User Details</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mt: 2, mb: 1 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mt: 2, mb: 1 }}>
            User created and invited successfully!
          </Alert>
        )}
        
        <TextField
          autoFocus
          margin="dense"
          label="First Name"
          fullWidth
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          disabled={loading}
          required
        />
        <TextField
          margin="dense"
          label="Last Name"
          fullWidth
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          disabled={loading}
          required
        />
        <TextField
          margin="dense"
          label="Password"
          type="password"
          fullWidth
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            validatePassword(e.target.value);
          }}
          disabled={loading}
          required
          error={!!passwordError}
        />
        {passwordError && (
          <FormHelperText error>{passwordError}</FormHelperText>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>Cancel</Button>
        <Button
          onClick={onSubmit}
          disabled={!firstName || !lastName || !password || !!passwordError || loading}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {loading ? 'Creating...' : 'Submit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewUserDetailsDialog; 